import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type KeyboardEvent, type TextareaHTMLAttributes } from "react";
import type { MentionCandidate } from "@atrium/shared";
import { api } from "../../lib/api";
import { cn } from "../../lib/cn";
import { UserAvatar } from "../../components/ui/Avatar";
import { UserBadges } from "../../components/UserBadges";

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  value: string;
  onChange: (v: string) => void;
  /** Grow with the content up to this height (px). */
  autoGrow?: number;
};

const cache = new Map<string, MentionCandidate[]>();

/**
 * Textarea with @mention autocomplete: typing "@" followed by letters shows
 * matching people (people you follow first). ↑/↓ choose, Enter or Tab inserts,
 * Escape closes.
 */
export const MentionTextarea = forwardRef<HTMLTextAreaElement, Props>(function MentionTextarea({ value, onChange, autoGrow, onKeyDown, className, ...rest }, ref) {
  const inner = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => inner.current!);
  const [query, setQuery] = useState<{ q: string; start: number } | null>(null);
  const [items, setItems] = useState<MentionCandidate[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const ta = inner.current;
    if (!ta || !autoGrow) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, autoGrow)}px`;
  }, [value, autoGrow]);

  useEffect(() => {
    if (!query) return setItems([]);
    const q = query.q.toLowerCase();
    if (cache.has(q)) {
      setItems(cache.get(q)!);
      setIndex(0);
      return;
    }
    const t = setTimeout(() => {
      void api
        .get<{ items: MentionCandidate[] }>(`/api/users/autocomplete?q=${encodeURIComponent(q)}`)
        .then((r) => {
          cache.set(q, r.items);
          setItems(r.items);
          setIndex(0);
        })
        .catch(() => setItems([]));
    }, 120);
    return () => clearTimeout(t);
  }, [query]);

  const detect = (text: string, caret: number) => {
    const before = text.slice(0, caret);
    const m = /(^|[\s(])@([a-zA-Z0-9_]{1,24})$/.exec(before);
    setQuery(m ? { q: m[2], start: caret - m[2].length - 1 } : null);
  };

  const insert = (u: MentionCandidate) => {
    const ta = inner.current;
    if (!ta || !query) return;
    const caret = ta.selectionStart;
    const next = `${value.slice(0, query.start)}@${u.username} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);
    const pos = query.start + u.username.length + 2;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(pos, pos);
    });
  };

  const open = !!query && items.length > 0;
  const keyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIndex((i) => (i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length);
        return;
      }
      if ((e.key === "Enter" && !e.metaKey && !e.ctrlKey) || e.key === "Tab") {
        e.preventDefault();
        insert(items[index]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setQuery(null);
        return;
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div className="relative">
      <textarea
        ref={inner}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          detect(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={keyDown}
        onBlur={() => setTimeout(() => setQuery(null), 150)}
        aria-autocomplete="list"
        aria-expanded={open}
        className={cn("block resize-none bg-transparent text-fg placeholder:text-fg-3 focus:outline-none", className)}
        {...rest}
      />
      {open && (
        <ul role="listbox" aria-label="People" className="absolute left-0 top-full z-[70] mt-1 w-72 max-w-full rounded-lg bg-surface p-1 shadow-pop animate-pop-in">
          {items.map((u, i) => (
            <li key={u.id} role="option" aria-selected={i === index}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(u);
                }}
                onMouseEnter={() => setIndex(i)}
                className={cn("flex h-10 w-full items-center gap-2.5 rounded-md px-2 text-left", i === index && "bg-surface-2")}
              >
                <UserAvatar user={u} size="sm" plain />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 truncate text-sm font-medium text-fg">
                    {u.displayName}
                    <UserBadges user={u} />
                  </span>
                  <span className="block truncate text-xs text-fg-3">@{u.username}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
